import {
  addDoc, collection, deleteDoc, doc,
  getDoc, getDocs, increment, onSnapshot, orderBy,
  query, serverTimestamp, setDoc, updateDoc, where
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";

import { db, functions } from "../firebase";
import { toggleFollow } from "./followService";

const onDropViewCallable = httpsCallable(functions, "onDropView");

/* ─── MOMENT LISTENER ─── */
export function listenToMoment(momentId, callback) {
  if (!momentId) return () => {};
  return onSnapshot(doc(db, "moments", momentId), (snap) => {
    if (!snap.exists()) return;
    callback({ id: snap.id, ...snap.data() });
  });
}

/* ─── VIEW TRACKING (server-side via Cloud Function) ─── */
/**
 * Record a view on a drop via the onDropView Cloud Function.
 * Client never writes to views or increments viewCount directly.
 */
export async function recordDropView({ dropId, userId }) {
  if (!dropId) return { alreadyViewed: true };

  if (!userId) {
    try {
      const snap = await getDoc(doc(db, "drops", dropId));
      if (!snap.exists()) return { alreadyViewed: false, anonymous: true };
      return {
        alreadyViewed: false,
        newViewCount: snap.data().viewCount || 0,
        anonymous: true,
      };
    } catch {
      return { alreadyViewed: false, anonymous: true };
    }
  }

  try {
    const { data } = await onDropViewCallable({ dropId });
    return data || { alreadyViewed: false };
  } catch (err) {
    console.error("onDropView error:", err);
    throw err;
  }
}

/** @deprecated Use recordDropView — kept for callers passing momentId */
export async function recordView({ momentId, dropId, userId }) {
  return recordDropView({ dropId: dropId || momentId, userId });
}

/**
 * Check whether a user has permanent first-view access to a drop/moment.
 */
export async function checkFirstViewAccess(userId, contentId) {
  if (!userId || !contentId) return false;
  try {
    const snap = await getDoc(doc(db, "first_views", `${contentId}_${userId}`));
    return snap.exists();
  } catch {
    return false;
  }
}

/* ─── REACTIONS ─── */
export async function checkUserReaction({ type, momentId, userId }) {
  if (!type || !momentId || !userId) return false;
  const snap = await getDoc(doc(db, type, `${userId}_${momentId}`));
  return snap.exists();
}

export async function toggleLike({ momentId, userId, isLiked }) {
  const likeRef = doc(db, "likes", `${userId}_${momentId}`);
  const momentRef = doc(db, "moments", momentId);
  if (isLiked) {
    await deleteDoc(likeRef);
    await updateDoc(momentRef, { likesCount: increment(-1) });
  } else {
    await setDoc(likeRef, { momentId, userId, createdAt: serverTimestamp() });
    await updateDoc(momentRef, { likesCount: increment(1) });
  }
}

export async function toggleSave({ momentId, userId, isSaved }) {
  const saveRef = doc(db, "saves", `${userId}_${momentId}`);
  const momentRef = doc(db, "moments", momentId);
  if (isSaved) {
    await deleteDoc(saveRef);
    await updateDoc(momentRef, { savesCount: increment(-1) });
  } else {
    let momentData = {};
    try {
      const momentSnap = await getDoc(momentRef);
      if (momentSnap.exists()) momentData = momentSnap.data();
    } catch { /* fall back to blank fields below */ }

    await setDoc(saveRef, {
      momentId,
      userId,
      creatorId: momentData.creatorId || null,
      creatorUsername: momentData.creatorUsername || "",
      caption: momentData.caption || "",
      mediaUrl: momentData.mediaUrl || "",
      createdAt: serverTimestamp(),
      savedAt: serverTimestamp()
    });
    await updateDoc(momentRef, { savesCount: increment(1) });
  }
}

/** @deprecated Use toggleFollow from followService — kept so older imports keep working. */
export async function toggleFollowWithBadge(opts) {
  return toggleFollow(opts);
}

/* ─── COMMENTS ─── */
export async function addComment({ momentId, userId, displayName, avatar, text }) {
  if (!momentId || !userId || !text?.trim()) throw new Error("Missing comment information.");
  await addDoc(collection(db, "comments"), {
    momentId, userId, displayName, avatar,
    text: text.trim(),
    createdAt: serverTimestamp()
  });
  await updateDoc(doc(db, "moments", momentId), { commentsCount: increment(1) });
}

export function listenToComments(momentId, callback) {
  if (!momentId) return () => {};
  return onSnapshot(
    query(collection(db, "comments"), where("momentId", "==", momentId), orderBy("createdAt", "asc")),
    (snap) => callback(snap.docs.map(d => ({ id: d.id, ...d.data() })))
  );
}

/* ─── DROP LISTENER & ENGAGEMENT ─── */
export function listenToDrop(dropId, callback) {
  if (!dropId) return () => {};
  return onSnapshot(doc(db, "drops", dropId), (snap) => {
    if (!snap.exists()) return;
    callback({ id: snap.id, ...snap.data() });
  });
}

export async function toggleDropLike({ dropId, userId, isLiked }) {
  const likeRef = doc(db, "likes", `${userId}_${dropId}`);
  const dropRef = doc(db, "drops", dropId);
  if (isLiked) {
    await deleteDoc(likeRef);
    await updateDoc(dropRef, { likesCount: increment(-1) });
  } else {
    await setDoc(likeRef, { dropId, momentId: dropId, userId, createdAt: serverTimestamp() });
    await updateDoc(dropRef, { likesCount: increment(1) });
  }
}

export async function toggleDropSave({ dropId, userId, isSaved, dropData = {} }) {
  const saveRef = doc(db, "saves", `${userId}_${dropId}`);
  const dropRef = doc(db, "drops", dropId);
  if (isSaved) {
    await deleteDoc(saveRef);
    await updateDoc(dropRef, { savesCount: increment(-1) });
  } else {
    await setDoc(saveRef, {
      dropId,
      momentId: dropId,
      userId,
      creatorId: dropData.creatorId || null,
      creatorUsername: dropData.creatorUsername || "",
      caption: dropData.title || dropData.description || "",
      mediaUrl: dropData.thumbnailUrl || dropData.mediaUrl || "",
      createdAt: serverTimestamp(),
      savedAt: serverTimestamp(),
    });
    await updateDoc(dropRef, { savesCount: increment(1) });
  }
}

/* ─── DROP COMMENTS (drops/{dropId}/comments) ─── */
const softDeleteDropCommentFn = httpsCallable(functions, "softDeleteDropComment");
const reportDropCommentFn = httpsCallable(functions, "reportDropComment");
const setCreatorCommentReplyFn = httpsCallable(functions, "setCreatorCommentReply");

export function listenToDropComments(dropId, callback) {
  if (!dropId) return () => {};
  return onSnapshot(
    query(collection(db, "drops", dropId, "comments"), orderBy("createdAt", "asc")),
    (snap) => {
      const rows = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((c) => c.deleted !== true);
      callback(rows);
    }
  );
}

export async function addDropComment({
  dropId,
  userId,
  displayName,
  avatar,
  text,
  creatorId = null,
  handle = "",
}) {
  if (!dropId || !userId || !text?.trim()) throw new Error("Missing comment information.");
  const trimmed = text.trim().slice(0, 280);
  const isCreator = Boolean(creatorId && userId === creatorId);
  await addDoc(collection(db, "drops", dropId, "comments"), {
    userId,
    displayName,
    avatar,
    handle: handle || "",
    text: trimmed,
    isCreator,
    likeCount: 0,
    likedBy: [],
    deleted: false,
    createdAt: serverTimestamp(),
  });
  await updateDoc(doc(db, "drops", dropId), { commentsCount: increment(1) });
}

/** Fan-facing comment like toggle — idempotent via likedBy array. */
export async function toggleDropCommentLike({ dropId, commentId, userId }) {
  if (!dropId || !commentId || !userId) throw new Error("Missing comment like info.");
  const ref = doc(db, "drops", dropId, "comments", commentId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Comment not found.");
  const data = snap.data();
  if (data.deleted === true) throw new Error("Comment not found.");
  const likedBy = Array.isArray(data.likedBy) ? [...data.likedBy] : [];
  const idx = likedBy.indexOf(userId);
  if (idx >= 0) {
    likedBy.splice(idx, 1);
  } else {
    likedBy.push(userId);
  }
  await updateDoc(ref, {
    likedBy,
    likeCount: likedBy.length,
  });
  return { liked: idx < 0, likeCount: likedBy.length };
}

export async function softDeleteDropComment({ dropId, commentId }) {
  if (!dropId || !commentId) throw new Error("Missing comment information.");
  const { data } = await softDeleteDropCommentFn({ dropId, commentId });
  return data;
}

export async function reportDropComment({ dropId, commentId, reason }) {
  if (!dropId || !commentId || !reason) throw new Error("Missing report information.");
  const { data } = await reportDropCommentFn({ dropId, commentId, reason });
  return data;
}

export async function setCreatorCommentReply({ dropId, commentId, text }) {
  if (!dropId || !commentId || !text?.trim()) throw new Error("Missing reply information.");
  const { data } = await setCreatorCommentReplyFn({
    dropId,
    commentId,
    text: text.trim().slice(0, 280),
  });
  return data;
}

export async function checkIsFollowing(followerId, creatorId) {
  if (!followerId || !creatorId) return false;
  try {
    const snap = await getDoc(doc(db, "follows", `${followerId}_${creatorId}`));
    return snap.exists();
  } catch {
    return false;
  }
}


/* ─── SUPPORTER ACCESS ─── */

export async function checkSupporterAccess(userId, creatorId) {
  if (!userId || !creatorId) return { isSupporter: false };
  if (userId === creatorId) return { isSupporter: true, isCreator: true };
  try {
    const snap = await getDocs(query(
      collection(db, "supporters"),
      where("userId", "==", userId),
      where("creatorId", "==", creatorId)
    ));
    if (snap.empty) return { isSupporter: false };
    const data = snap.docs[0].data();
    return { isSupporter: true, tierName: data.tierName || "Supporter", tierId: data.tierId };
  } catch (err) {
    console.error("Supporter check error:", err);
    return { isSupporter: false };
  }
}

export async function checkCreditUnlock(userId, momentId) {
  if (!userId || !momentId) return false;
  try {
    const snap = await getDoc(doc(db, "creditUnlocks", `${momentId}_${userId}`));
    return snap.exists();
  } catch { return false; }
}

export async function unlockWithCredits(userId, momentId, creditCost) {
  if (!userId || !momentId) return { success: false, error: "Missing info" };
  try {
    const perksRef = doc(db, "userPerks", userId);
    const perksSnap = await getDoc(perksRef);
    if (!perksSnap.exists()) return { success: false, error: "No credits account" };

    const credits = perksSnap.data().credits || 0;
    if (credits < creditCost) return { success: false, error: "Not enough credits", credits };

    await Promise.all([
      updateDoc(perksRef, {
        credits: increment(-creditCost),
        totalCreditsSpent: increment(creditCost),
        updatedAt: serverTimestamp()
      }),
      setDoc(doc(db, "creditUnlocks", `${momentId}_${userId}`), {
        momentId, userId, creditCost,
        unlockedAt: serverTimestamp()
      })
    ]);

    return { success: true, newBalance: credits - creditCost };
  } catch (err) {
    console.error("Credit unlock error:", err);
    return { success: false, error: "Unlock failed" };
  }
}
